import { Link, useRouterState } from "@tanstack/react-router";
import {
  Bell,
  LogOut,
  Search,
  ShieldCheck,
  ExternalLink,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { useState, type ComponentType, type ReactNode } from "react";
import { daanAmrutLogo } from "@/project/data/sample";

const logoUrl = daanAmrutLogo;

export type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
};

export function DashboardShell({
  role,
  color,
  items,
  children,
  orgName,
}: {
  role: string;
  color: string;
  items: NavItem[];
  children: ReactNode;
  orgName?: string;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const active = items.find(
    (i) =>
      i.to === pathname ||
      (i.to !== "/admin" &&
        i.to !== "/donor" &&
        i.to !== "/institution" &&
        pathname.startsWith(i.to))
  );

  return (
    <div className="flex min-h-screen bg-[var(--cream-2)]">
      {/* ── Desktop Sidebar (Vibrant Brand Light Style) ───────────────────────── */}
      <aside
        className={`hidden md:flex shrink-0 flex-col bg-white text-foreground sticky top-0 h-screen overflow-y-auto border-r border-border/80 transition-all duration-300 ${
          collapsed ? "w-20" : "w-64"
        }`}
      >
        {/* Brand Accent Top Line */}
        <div className="h-1 w-full bg-gradient-to-r from-[var(--maroon)] via-[var(--saffron-deep)] to-[var(--maroon)]" />

        {/* Sidebar Header / Logo */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-border/60">
          <Link to="/" className="flex items-center gap-3 min-w-0">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--cream-2)] p-1.5 border border-border/60 shadow-xs">
              <img
                src={logoUrl}
                alt="Daan Amrut"
                className="h-full w-auto object-contain"
              />
            </span>
            {!collapsed && (
              <span className="flex flex-col leading-tight min-w-0">
                <span className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-[var(--saffron-deep)] truncate">
                  Daan Amrut
                </span>
                <span className="font-display text-sm font-extrabold truncate text-[var(--maroon-deep)]">
                  Console
                </span>
              </span>
            )}
          </Link>
          <button
            onClick={() => setCollapsed((v) => !v)}
            className="hidden lg:grid h-7 w-7 place-items-center rounded-lg bg-muted/60 hover:bg-muted text-foreground/75 transition border border-border/40"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* User Role Card */}
        {!collapsed && (
          <div className="mx-3 mt-3 rounded-2xl bg-[var(--saffron)]/10 p-3.5 border border-[var(--saffron-deep)]/25 space-y-1">
            <p className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-[var(--saffron-deep)]">
              Signed in as
            </p>
            <p className="text-sm font-extrabold truncate text-[var(--maroon-deep)]">
              {role}
            </p>
            {orgName && (
              <p className="text-[11px] font-medium text-foreground/70 truncate">
                {orgName}
              </p>
            )}
            <div className="pt-1.5 flex items-center gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--maroon)] px-2 py-0.5 text-[10px] font-extrabold text-white shadow-xs">
                <ShieldCheck className="h-3 w-3 text-[var(--saffron)]" /> Verified
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-[var(--maroon-deep)] border border-border/60">
                <Sparkles className="h-2.5 w-2.5 text-[var(--saffron-deep)]" /> AI Active
              </span>
            </div>
          </div>
        )}

        {/* Navigation Items */}
        <nav className="mt-3 flex-1 space-y-1.5 px-3">
          {items.map((n) => {
            const isItemActive =
              pathname === n.to ||
              (n.to !== "/admin" &&
                n.to !== "/donor" &&
                n.to !== "/institution" &&
                pathname.startsWith(n.to));
            return (
              <Link
                key={n.to}
                to={n.to}
                title={collapsed ? n.label : undefined}
                className={`group flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-all ${
                  isItemActive
                    ? "bg-[var(--maroon)] text-white font-bold shadow-md shadow-[var(--maroon)]/20"
                    : "text-foreground/80 hover:bg-[var(--cream-2)] hover:text-[var(--maroon-deep)] font-semibold"
                }`}
              >
                <div
                  className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors ${
                    isItemActive
                      ? "bg-white/15 text-[var(--saffron)]"
                      : "bg-[var(--saffron)]/15 text-[var(--maroon-deep)] group-hover:bg-[var(--maroon)] group-hover:text-white"
                  }`}
                >
                  <n.icon className="h-4 w-4" />
                </div>
                {!collapsed && <span className="truncate">{n.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar Footer Quick Links */}
        <div className="m-3 space-y-2 pt-2 border-t border-border/60">
          <Link
            to="/"
            className="flex items-center justify-between rounded-xl border border-border/60 bg-[var(--cream-2)] px-3 py-2 text-xs font-bold text-[var(--maroon-deep)] hover:bg-white transition shadow-xs"
          >
            {!collapsed && <span>Public Website</span>}
            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-[var(--maroon)]" />
          </Link>
          <Link
            to="/demo"
            className="flex items-center gap-2.5 rounded-xl border border-border/60 px-3 py-2 text-xs font-semibold text-foreground/75 hover:bg-muted transition"
          >
            <LogOut className="h-3.5 w-3.5 shrink-0 text-foreground/50" />
            {!collapsed && <span>Switch Role</span>}
          </Link>
        </div>
      </aside>

      {/* ── Main Content Container ────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Console Header Bar */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-border bg-background/95 px-4 backdrop-blur sm:px-6">
          <div className="flex items-center gap-2.5 min-w-0">
            {/* Mobile Drawer Trigger Button */}
            <button
              onClick={() => setMobileDrawerOpen(true)}
              className="md:hidden grid h-9 w-9 place-items-center rounded-xl border border-border bg-muted/40 hover:bg-muted text-foreground"
              aria-label="Open Sidebar Menu"
            >
              <Menu className="h-4 w-4" />
            </button>

            <Link to="/" className="flex items-center gap-2 md:hidden shrink-0">
              <img
                src={logoUrl}
                alt="Daan Amrut"
                className="h-7 w-auto object-contain shrink-0"
              />
              <span className="text-xs font-bold text-[var(--maroon-deep)] truncate max-w-[110px]">
                {role.split(" ")[0]} Console
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-2 text-xs text-foreground/70">
              <span>Foundation Console</span>
              <span>/</span>
              <span className="font-bold text-foreground">
                {active?.label ?? "Dashboard"}
              </span>
            </div>
          </div>

          <div className="hidden md:flex flex-1 max-w-md items-center gap-2 rounded-full border border-border bg-[var(--cream-2)] px-3.5 py-1.5">
            <Search className="h-3.5 w-3.5 text-foreground/60" />
            <input
              placeholder="Search causes, donors, institutions..."
              className="w-full bg-transparent text-xs outline-none placeholder:text-foreground/60"
            />
            <kbd className="hidden lg:inline rounded bg-white px-1.5 py-0.5 text-[10px] font-semibold text-foreground/70 ring-1 ring-border">
              ⌘K
            </kbd>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              aria-label="Notifications"
              className="relative grid h-8 w-8 place-items-center rounded-full border border-border hover:bg-[var(--cream-2)] transition"
            >
              <Bell className="h-4 w-4 text-foreground/70" />
              <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-[var(--saffron-deep)]" />
            </button>
            <div className="flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 bg-background">
              <span
                className="grid h-5 w-5 place-items-center rounded-full text-[9px] font-bold text-white shadow-xs"
                style={{ background: color }}
              >
                {role.charAt(0)}
              </span>
              <span className="text-xs font-semibold hidden sm:inline text-foreground">
                {role.split(" ")[0]}
              </span>
            </div>
            <Link
              to="/demo"
              className="text-xs font-bold text-[var(--maroon)] hover:underline ml-1"
            >
              Switch
            </Link>
          </div>
        </header>

        {/* Mobile Horizontal Scrollable Nav Tabs */}
        <div className="md:hidden border-b border-border bg-background sticky top-14 z-20 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none]">
          <div className="flex gap-1.5 px-3 py-2 min-w-max">
            {items.map((n) => {
              const isItemActive =
                pathname === n.to ||
                (n.to !== "/admin" &&
                  n.to !== "/donor" &&
                  n.to !== "/institution" &&
                  pathname.startsWith(n.to));
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`shrink-0 flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${
                    isItemActive
                      ? "bg-[var(--maroon)] text-[var(--cream)] shadow-sm"
                      : "text-foreground/75 bg-muted/50 hover:bg-muted"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{n.label}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Main Content View */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-20 md:pb-8">
          {children}
        </main>

        <footer className="border-t border-border/60 bg-background/70 px-4 py-3 text-[11px] text-muted-foreground mb-14 md:mb-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              © {new Date().getFullYear()} Daan Amrut Foundation · Console v2.0
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--success)]" />{" "}
              Verified Live
            </span>
          </div>
        </footer>
      </div>

      {/* ── Mobile Sidebar Drawer Overlay ────────────────────────────── */}
      {mobileDrawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity"
            onClick={() => setMobileDrawerOpen(false)}
          />

          {/* Drawer Slide Panel */}
          <div className="relative flex w-4/5 max-w-xs flex-1 flex-col bg-[var(--maroon-deep)] text-[var(--cream)] shadow-2xl transition-transform">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-white p-1.5">
                  <img
                    src={logoUrl}
                    alt="Daan Amrut"
                    className="h-full w-auto object-contain"
                  />
                </span>
                <div>
                  <h3 className="font-display text-sm font-extrabold text-white">
                    Daan Amrut
                  </h3>
                  <p className="text-[10px] text-[var(--saffron)] uppercase tracking-wider">
                    {role} Console
                  </p>
                </div>
              </div>
              <button
                onClick={() => setMobileDrawerOpen(false)}
                className="grid h-8 w-8 place-items-center rounded-lg bg-white/10 text-white hover:bg-white/20"
                aria-label="Close Mobile Sidebar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="rounded-xl bg-white/8 p-3 ring-1 ring-white/10 space-y-1">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[var(--saffron)]">
                  Signed in as
                </p>
                <p className="text-xs font-bold text-white">{role}</p>
                {orgName && (
                  <p className="text-[11px] text-white/70">{orgName}</p>
                )}
              </div>

              <nav className="space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-white/50 mb-2 px-1">
                  Console Modules
                </p>
                {items.map((n) => {
                  const isItemActive =
                    pathname === n.to ||
                    (n.to !== "/admin" &&
                      n.to !== "/donor" &&
                      n.to !== "/institution" &&
                      pathname.startsWith(n.to));
                  const Icon = n.icon;
                  return (
                    <Link
                      key={n.to}
                      to={n.to}
                      onClick={() => setMobileDrawerOpen(false)}
                      className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-xs font-semibold transition ${
                        isItemActive
                          ? "bg-white/15 text-white font-bold"
                          : "text-white/75 hover:bg-white/8 hover:text-white"
                      }`}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-[var(--saffron)]" />
                      <span>{n.label}</span>
                    </Link>
                  );
                })}
              </nav>

              <div className="pt-3 border-t border-white/10 space-y-2">
                <Link
                  to="/"
                  onClick={() => setMobileDrawerOpen(false)}
                  className="flex items-center justify-between rounded-xl border border-white/10 px-3 py-2.5 text-xs text-white/80 hover:bg-white/8"
                >
                  <span>Public Site</span>
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to="/demo"
                  onClick={() => setMobileDrawerOpen(false)}
                  className="flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2.5 text-xs text-white/80 hover:bg-white/8"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  <span>Switch Role</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components for Console Pages ──────────────────────────────────────

export function PageHead({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-1 text-xs sm:text-sm text-muted-foreground max-w-2xl leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function Panel({
  title,
  action,
  children,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border/80 bg-card p-4 sm:p-6 shadow-xs">
      {(title || action) && (
        <div className="mb-4 flex items-center justify-between gap-2 border-b border-border/60 pb-3">
          {title && (
            <h3 className="font-display text-sm font-bold text-foreground">
              {title}
            </h3>
          )}
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

export function StatCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "primary" | "good" | "warn" | "danger";
}) {
  const toneColors = {
    primary: "text-[var(--maroon-deep)]",
    good: "text-[var(--success)]",
    warn: "text-[var(--saffron-deep)]",
    danger: "text-red-600",
  };
  const borderTone = {
    primary: "bg-[var(--maroon)]",
    good: "bg-[var(--success)]",
    warn: "bg-[var(--saffron-deep)]",
    danger: "bg-red-500",
  };
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border/80 bg-card p-4 shadow-xs">
      {tone && <div className={`absolute top-0 inset-x-0 h-1 ${borderTone[tone]}`} />}
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
        {label}
      </p>
      <p
        className={`mt-2 font-display text-2xl font-extrabold ${
          tone ? toneColors[tone] : "text-foreground"
        }`}
      >
        {value}
      </p>
      {hint && <p className="mt-1 text-[11px] font-medium text-muted-foreground">{hint}</p>}
    </div>
  );
}